import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Collapse,
  useTheme,
  alpha,
} from '@mui/material';
import {
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Payment as PaymentIcon,
  CloudUpload as UploadIcon,
  Verified as VerifiedIcon,
  Send as SendIcon,
  Timeline as TimelineIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import WeChatQRDialog from '../common/WeChatQRDialog';
import useWeChatQR from '../../hooks/useWeChatQR';

const ApplicationFlow = ({ onServiceSelect, isAuthenticated = true, onRequireLogin }) => {
  const theme = useTheme();
  const [expandedStep, setExpandedStep] = useState(null);
  const { openQR, openWeChatQR, closeWeChatQR } = useWeChatQR();

  const applicationSteps = [
    {
      id: 1,
      title: '专业与院校选择',
      subtitle: '找到最适合的学习方向',
      icon: <SchoolIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.primary.main,
      duration: '1-2天',
      description: '基于您的背景、兴趣和职业规划，我们的专业顾问将为您推荐最合适的专业和院校。',
      details: [
        '个人背景评估',
        '专业兴趣分析',
        '职业规划咨询',
        '院校匹配推荐',
        '入学要求解析',
      ],
      tips: '💡 建议提前准备成绩单和语言成绩，有助于更精准的匹配'
    },
    {
      id: 2,
      title: '服务套餐选择',
      subtitle: '选择最适合的服务方案',
      icon: <AssignmentIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.secondary.main,
      duration: '即时',
      description: '根据您的需求和预算，选择合适的申请服务套餐，确保获得最佳的申请支持。',
      details: [
        '套餐对比分析',
        '服务内容详解',
        '费用明细说明',
        '付款方式选择',
        '合同条款确认',
      ],
      tips: '💰 推荐选择全程无忧套餐，性价比最高'
    },
    {
      id: 3,
      title: '个人信息收集',
      subtitle: '建立完整的申请档案',
      icon: <VerifiedIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
      duration: '2-3天',
      description: '收集和整理您的个人信息、学术背景、工作经历等申请所需的基础资料。',
      details: [
        '个人基本信息',
        '教育背景详情',
        '工作经历整理',
        '获奖证书收集',
        '推荐人信息',
      ],
      tips: '📋 信息越详细，申请材料质量越高'
    },
    {
      id: 4,
      title: '费用支付确认',
      subtitle: '完成服务费用支付',
      icon: <PaymentIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.warning.main,
      duration: '即时',
      description: '选择便捷的支付方式完成服务费用支付，开启正式的申请服务流程。',
      details: [
        '支付方式选择',
        '费用明细确认',
        '分期付款安排',
        '发票开具',
        '支付凭证保存',
      ],
      tips: '💳 支持微信、支付宝、银行转账等多种支付方式'
    },
    {
      id: 5,
      title: '申请材料准备',
      subtitle: '专业指导文书写作',
      icon: <UploadIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.info.main,
      duration: '2-4周',
      description: '在专业顾问指导下，准备和完善所有申请材料，包括文书写作和证明文件。',
      details: [
        '个人陈述指导',
        '推荐信模板',
        '简历优化',
        '证明文件准备',
        '材料翻译公证',
      ],
      tips: '✍️ 文书写作是申请成功的关键，建议多次修改完善'
    },
    {
      id: 6,
      title: '正式提交申请',
      subtitle: '向目标院校提交申请',
      icon: <SendIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.primary.main,
      duration: '1-2天',
      description: '材料审核无误后，正式向选定的院校提交申请，开始等待录取结果。',
      details: [
        '材料最终审核',
        '申请表填写',
        '网上申请提交',
        '申请费用缴纳',
        '提交确认回执',
      ],
      tips: '🚀 提交后定期检查申请状态和邮件通知'
    },
    {
      id: 7,
      title: '进度跟踪与结果',
      subtitle: '全程跟踪申请进展',
      icon: <TimelineIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
      duration: '4-8周',
      description: '实时跟踪申请进度，及时沟通院校反馈，直到收到录取通知书。',
      details: [
        '申请状态跟踪',
        '院校沟通联络',
        '补充材料提交',
        '面试安排协助',
        '录取结果通知',
      ],
      tips: '📊 可通过专属平台随时查看申请进度'
    },
  ];

  const handleExpandClick = (stepId) => {
    setExpandedStep(expandedStep === stepId ? null : stepId);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box id="application-process-steps">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        
      >
        {/* Process Overview */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="h4"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            详细申请流程
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            点击每个步骤了解详细信息，或直接开始您的申请之旅
          </Typography>
        </Box>

        {/* Application Steps */}
        <Grid container spacing={3}>
          {applicationSteps.map((step, index) => (
            <Grid item xs={12} key={step.id}>
              <motion.div variants={cardVariants}>
                <Card
                  sx={{
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    border: `2px solid ${alpha(step.color, 0.1)}`,
                    '&:hover': {
                      borderColor: alpha(step.color, 0.3),
                      boxShadow: `0 8px 32px ${alpha(step.color, 0.15)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    {/* Step Header */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 3,
                        cursor: 'pointer',
                      }}
                      onClick={() => handleExpandClick(step.id)}
                    >
                      {/* Step Number */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          backgroundColor: alpha(step.color, 0.1),
                          color: step.color,
                          mr: 3,
                          fontSize: '1.5rem',
                          fontWeight: 700,
                        }}
                      >
                        {step.id}
                      </Box>

                      {/* Step Icon */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          backgroundColor: alpha(step.color, 0.1),
                          color: step.color,
                          mr: 3,
                        }}
                      >
                        {step.icon}
                      </Box>

                      {/* Step Info */}
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            mb: 0.5,
                          }}
                        >
                          {step.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: theme.palette.text.secondary,
                            mb: 1,
                          }}
                        >
                          {step.subtitle}
                        </Typography>
                        <Chip
                          label={`预计用时：${step.duration}`}
                          size="small"
                          sx={{
                            backgroundColor: alpha(step.color, 0.1),
                            color: step.color,
                            fontWeight: 500,
                          }}
                        />
                      </Box>

                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                        {step.id === 1 && (
                          <Button
                            variant="contained"
                            startIcon={<PlayIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isAuthenticated) {
                                onRequireLogin && onRequireLogin();
                                return;
                              }
                              const el = document.getElementById('application-process');
                              if (el) {
                                el.scrollIntoView({ behavior: 'smooth' });
                              }
                              onServiceSelect && onServiceSelect('basic');
                            }}
                            sx={{
                              backgroundColor: step.color,
                              borderRadius: 20,
                              px: 3,
                              '&:hover': {
                                backgroundColor: step.color,
                                opacity: 0.9,
                              },
                            }}
                          >
                            开始申请
                          </Button>
                        )}
                      </Box>

                      {/* Expand Icon */}
                      <IconButton
                        sx={{
                          transform: expandedStep === step.id ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease',
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </Box>

                    {/* Step Details */}
                    <Collapse in={expandedStep === step.id} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 3, pb: 3, pt: 0 }}>
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            backgroundColor: alpha(step.color, 0.05),
                            border: `1px solid ${alpha(step.color, 0.1)}`,
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              mb: 3,
                              color: theme.palette.text.primary,
                              lineHeight: 1.6,
                            }}
                          >
                            {step.description}
                          </Typography>

                          <Typography
                            variant="h6"
                            sx={{
                              mb: 2,
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                            }}
                          >
                            详细内容：
                          </Typography>

                          <Grid container spacing={2} sx={{ mb: 3 }}>
                            {step.details.map((detail, detailIndex) => (
                              <Grid item xs={12} sm={6} md={4} key={detailIndex}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 1.5,
                                    borderRadius: 1,
                                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      backgroundColor: step.color,
                                      mr: 1.5,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{ color: theme.palette.text.primary }}
                                  >
                                    {detail}
                                  </Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>

                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 1,
                              backgroundColor: alpha(theme.palette.info.main, 0.1),
                              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.info.main,
                                fontWeight: 500,
                              }}
                            >
                              {step.tips}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* CTA Section */}
        <Box
          sx={{
            textAlign: 'center',
            mt: 6,
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              mb: 2,
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            准备好开始您的申请了吗？
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              color: theme.palette.text.secondary,
            }}
          >
            我们的专业团队已经准备好为您提供全程指导
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayIcon />}
              onClick={() => {
                if (!isAuthenticated) {
                  onRequireLogin && onRequireLogin();
                  return;
                }
                const el = document.getElementById('application-process');
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth' });
                }
                onServiceSelect && onServiceSelect('premium');
              }}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 25,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
            >
              立即开始申请
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={openWeChatQR}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 25,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
            >
              预约咨询
            </Button>
          </Box>
        </Box>
      </motion.div>

      <WeChatQRDialog 
        open={openQR} 
        onClose={closeWeChatQR}
        title="预约咨询"
      />
    </Box>
  );
};

export default ApplicationFlow;
